import { useState, useEffect, useRef, Fragment, Suspense } from 'react';
import axios from 'axios';
import dynamic from 'next/dynamic';
import { motion } from 'framer-motion';
import { useLocationState } from '../../context/location';
import Skelet from '../Skelet';
import { categoryCaseConverter, declOfNum, sleep } from '../../helpers';
import { useObjectsProvider } from '../../context/objects';
import ObjectsMapCatalogSkeletonItem from './ObjectsMapCatalogSkeletonItem';
import ObjectsMapCatalogCommercialItem from './ObjectsMapCatalogCommercialItem';
import css from '../../styles/modules/objects/ObjectsMapCatalog.module.scss';

const ObjectsMapCatalogItem = dynamic(() => import('./ObjectsMapCatalogItem'), { suspense: true });

const ObjectsMapCatalog = () => {
  /** STATES */
  const locationState = useLocationState();
  const [objectsState, setObjectsState] = useObjectsProvider();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(null);

  /** REFS */
  const catalogEl = useRef(null);

  /** FUNCTIONS */
  const handleOnCloseClick = () => {
    setObjectsState({
      type: 'SET_FILTER',
      payload: {
        ...objectsState.filter,
        ads: []
      }
    });
    setObjectsState({
      type: 'SET_STATE',
      payload: {
        ...objectsState.state,
        isMapCatalogVisible: false
      }
    });
  };

  /** HOOKS */
  useEffect(() => {
    if (!objectsState.state.isMapCatalogVisible) return;
    if (catalogEl.current) catalogEl.current.scrollTo({ top: 0, behavior: 'smooth' });

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        await sleep(300);

        const response = await axios.post(process.env.NEXT_PUBLIC_API + '/objects', {
          ...objectsState.filter,
          ...objectsState.sorting,
          ...objectsState.map,
          location: locationState.region,
          page: 1,
          length: 100
        }, { signal });

        if (!response.data || response.data.status !== 'ok')
          throw new Error(response.data.text);
  
        setTotal(response.data.data.total);
        setData(response.data.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoading(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [objectsState.filter]);

  /** TEMPLATE */
  return (
    <motion.div
      initial={false}
      animate={{
        x: objectsState.state.isMapCatalogVisible ? 0 : -500,
        transition: { duration: 0.5 }
      }}
      className={css.wrapper}
    >
      <button
        type="button"
        title="Закрыть список"
        className={css.switch}
        onClick={handleOnCloseClick}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
      <div className={css.catalog} style={{ overflow: isLoading || isError ? 'hidden' : 'auto' }} ref={catalogEl}>
        {isLoading || isError ? (
          <Fragment>
            <div style={{ display: 'flex' }}>
              <Skelet height={26} style={{ maxWidth: '240px', width: '100%', margin: '18px auto' }} />
            </div>
            <div className={css.catalogContent}>
              {[...Array(5)].map((_, index) => <ObjectsMapCatalogSkeletonItem key={index} />)}
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <div className={css.catalogHeader}>
              {declOfNum(total, [objectsState.filter.category === 'дом' || objectsState.filter.category === 'участок' ? 'Найден' : 'Найдена', 'Найдено', 'Найдено'])}{' '}
              {total} {categoryCaseConverter(objectsState.filter.category, total)}
            </div>
            <div className={css.catalogContent}>
              {data.map((value, index) => {
                if (data.length >= 4 && index > 4 && index % 5 === 0) {
                  return (
                    <Fragment key={index}>
                      <Suspense fallback={<ObjectsMapCatalogSkeletonItem />}>
                        <ObjectsMapCatalogItem item={value} />
                      </Suspense>
                      {/* <ObjectsMapCatalogCommercialItem /> */}
                    </Fragment>
                  );
                }

                return (
                  <Suspense fallback={<ObjectsMapCatalogSkeletonItem />} key={index}>
                    <ObjectsMapCatalogItem item={value} />
                  </Suspense>);
              })}
            </div>
          </Fragment>
        )}
      </div>
    </motion.div>
  );
};

export default ObjectsMapCatalog;
